<template>
  <div class="api-details">
    <v-row align="center">
      <v-col>
        <v-breadcrumbs :items="breadcrumb" large></v-breadcrumbs>
      </v-col>
      <v-col class="text-right">
        <v-btn color="warning" dark class="mr-4" @click="retrieveCreds">
          <v-icon left>vpn_key</v-icon>
          <span>Credentials</span>
        </v-btn>
        <v-btn color="success" dark class="mr-4" @click="openSwagger">
          <v-icon left>tv</v-icon>
          <span>Swagger UI</span>
        </v-btn>
        <v-btn color="primary" icon dark class="mr-4" @click="refresh">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-col>
    </v-row>
    <v-divider></v-divider>
    <v-banner color="error" dark v-if="fetchOneError">
      <v-icon slot="icon" color="white" size="36">mdi-wifi-strength-alert-outline</v-icon>
      {{ errorDisplay(fetchOneError) }}
      <template v-slot:actions>
        <v-btn text color="dark" @click="refresh">Retry</v-btn>
      </template>
    </v-banner>

    <v-tabs v-model="tab" background-color="primary" dark icons-and-text grow>
      <v-tab href="#tab-settings">
        <span class="mt-2">Settings</span>
        <v-icon>settings</v-icon>
      </v-tab>
      <v-tab-item value="tab-settings" class="grey lighten-5">
        <v-container>
          <APIEditionForm
            :uuid="api ? api.uuid : ''"
            :id="api ? api.application_id: ''"
            :name="api ? api.application_name: ''"
            :description="api ? api.application_description: ''"
            :rootUrl="api ? api.root_url: ''"
            :redirectUris="api ? api.redirect_uris: []"
            :webOrigins="api ? api.web_origins: []"
            v-on:save="updateApi"
            ref="apiEditionForm"
          />
        </v-container>
      </v-tab-item>
      <v-tab href="#tab-scopes">
        <span class="mt-2">Scopes</span>
        <v-icon>pages</v-icon>
      </v-tab>
      <v-tab-item value="tab-scopes" class="grey lighten-5">
        <v-container fluid>
          <APIScopesPicker
            :availableScopes="availableScopes"
            :optionalScopes="optionalScopes"
            :defaultScopes="defaultScopes"
            v-on:AddDefault="addDefaultScopeRequest"
            v-on:addOptional="addOptionalScopeRequest"
            v-on:removeDefault="removeDefaultScopeRequest"
            v-on:removeOptional="removeOptionalScopeRequest"
          />
        </v-container>
      </v-tab-item>
      <v-tab href="#tab-service-account-roles">
        <span class="mt-2">Service Account Roles</span>
        <v-icon>policy</v-icon>
      </v-tab>
      <v-tab-item value="tab-service-account-roles" class="grey lighten-5">
        <v-container fluid>
          <APIRolesPicker
            :currentRoles="serviceAccountRoles"
            :availableRoles="serviceAccountAvailableRoles"
            v-on:addAvailable="addAvailableRole"
            v-on:removeCurrent="removeCurrentRole"
          />
        </v-container>
      </v-tab-item>
    </v-tabs>
    <v-snackbar v-model="snackbar" top>
      {{ errorMessage }}
      <v-btn color="red" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>
    <CredentialsDialog
      :dialog="credentialsDialog"
      :clientId="api ? api.application_id: ''"
      :clientSecret="clientSecret"
      v-on:close="closeCreds"
    />
    <SwaggerDialog :dialog="swaggerDialog" v-on:close="closeSwagger" :url="api ? api.root_url: ''" />
  </div>
</template>

<script>
import { mapState, mapActions } from "vuex";
import mixin from "@/mixins";
import APIEditionForm from "@/components/Admin/APIs/APIEditionForm";
import CredentialsDialog from "@/components/Admin/APIs/CredentialsDialog";
import SwaggerDialog from "@/components/Admin/APIs/SwaggerDialog";
import APIScopesPicker from "@/components/Admin/APIs/APIScopesPicker";
import APIRolesPicker from "@/components/Admin/APIs/APIRolesPicker";

export default {
  mixins: [mixin],
  components: {
    APIEditionForm,
    CredentialsDialog,
    SwaggerDialog,
    APIScopesPicker,
    APIRolesPicker
  },
  data() {
    return {
      apiUuid: this.$route.params.apiId,
      api: null,
      credentialsDialog: false,
      clientSecret: "",
      swaggerDialog: false,
      snackbar: false,
      errorMessage: "",
      tab: null,
      defaultScopes: [],
      optionalScopes: [],
      availableScopes: [],
      serviceAccountRoles: [],
      serviceAccountAvailableRoles: []
    };
  },
  computed: {
    ...mapState({
      fetchOneError: state => state.apis.fetchOne.error,
      fetchOneLoading: state => state.apis.fetchOne.pending,
      credsLoading: state => state.apis.retrieveCredentials.pending
    }),
    breadcrumb() {
      return [
        {
          text: "APIs",
          disabled: false,
          href: "#/admin/apis"
        },
        {
          text: this.api ? this.api.application_id : "unknown",
          disabled: true
        }
      ];
    }
  },
  methods: {
    ...mapActions("apis", { fetchOne: "fetchOne" }),
    ...mapActions("apis", { updateOne: "updateOne" }),
    ...mapActions("apis", { retrieveCredentials: "retrieveCredentials" }),
    ...mapActions("apis", { getDefaultScopes: "getDefaultScopes" }),
    ...mapActions("scopes", { fetchAllScopes: "fetchAll" }),
    ...mapActions("apis", { addDefaultScope: "addDefaultScope" }),
    ...mapActions("apis", { removeDefaultScope: "removeDefaultScope" }),
    ...mapActions("apis", { getOptionalScopes: "getOptionalScopes" }),
    ...mapActions("apis", { addOptionalScope: "addOptionalScope" }),
    ...mapActions("apis", { removeOptionalScope: "removeOptionalScope" }),
    ...mapActions("apis", {
      fetchAllServiceAccountRoles: "fetchAllServiceAccountRoles"
    }),
    ...mapActions("apis", {
      fetchAllServiceAccountAvailableRoles:
        "fetchAllServiceAccountAvailableRoles"
    }),
    ...mapActions("apis", { addServiceAccountRoles: "addServiceAccountRoles" }),
    ...mapActions("apis", {
      removeServiceAccountRoles: "removeServiceAccountRoles"
    }),
    refresh() {
      this.fetchOne({ uuid: this.apiUuid })
        .then(api => {
          this.api = api;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.refreshScopes();
      this.refreshRoles();
    },
    refreshScopes() {
      this.getDefaultScopes({ uuid: this.apiUuid })
        .then(scopes => {
          this.defaultScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.getOptionalScopes({ uuid: this.apiUuid })
        .then(scopes => {
          this.optionalScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllScopes()
        .then(scopes => {
          this.availableScopes = scopes;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    refreshRoles() {
      this.fetchAllServiceAccountRoles({ uuid: this.apiUuid })
        .then(roles => {
          this.serviceAccountRoles = roles;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
      this.fetchAllServiceAccountAvailableRoles({ uuid: this.apiUuid })
        .then(roles => {
          this.serviceAccountAvailableRoles = roles;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    updateApi(id, name, description, url, redirectUris, webOrigins) {
      this.updateOne({
        uuid: this.$route.params.apiId,
        id: id,
        name: name,
        description: description,
        url: url,
        redirectUris: redirectUris,
        webOrigins: webOrigins
      })
        .then(() => {
          this.refresh();
          this.$refs.apiEditionForm.saveCallback();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    retrieveCreds() {
      this.retrieveCredentials({ uuid: this.$route.params.apiId })
        .then(response => {
          this.clientSecret = response.client_secret;
          this.credentialsDialog = true;
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    closeCreds() {
      this.credentialsDialog = false;
      this.clientSecret = "";
    },
    openSwagger() {
      this.swaggerDialog = true;
    },
    closeSwagger() {
      this.swaggerDialog = false;
    },
    addDefaultScopeRequest(scopeUuid) {
      this.addDefaultScope({ uuid: this.apiUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addOptionalScopeRequest(scopeUuid) {
      this.addOptionalScope({ uuid: this.apiUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeDefaultScopeRequest(scopeUuid) {
      this.removeDefaultScope({ uuid: this.apiUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeOptionalScopeRequest(scopeUuid) {
      this.removeOptionalScope({ uuid: this.apiUuid, scopeUuid: scopeUuid })
        .then(() => {
          this.refreshScopes();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    addAvailableRole(uuid, name) {
      this.addServiceAccountRoles({
        uuid: this.apiUuid,
        roles: [{ uuid: uuid, name: name }]
      })
        .then(() => {
          this.refreshRoles();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    },
    removeCurrentRole(uuid, name) {
      this.removeServiceAccountRoles({
        uuid: this.apiUuid,
        roles: [{ uuid: uuid, name: name }]
      })
        .then(() => {
          this.refreshRoles();
        })
        .catch(error => {
          this.snackbar = true;
          this.errorMessage = this.errorDisplay(error);
        });
    }
  },
  created() {
    this.refresh();
  }
};
</script>

<style>
</style>